import { useState } from "react";
import { property } from "../data/property";
import { Share2, Copy, Check, Mail, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const SHARE_URL =
  typeof window !== "undefined" ? window.location.href : property.fundaUrl;
const SHARE_TITLE = `${property.address}, ${property.city} — ${property.price} ${property.priceSuffix}`;
const SHARE_TEXT = `Bekijk deze prachtige woning: ${property.address}, ${property.city}. ${property.tagline}`;

export default function ShareBar() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      trackEvent("share_success", { method: "copy_link" });
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  const handleNativeShare = async () => {
    trackEvent("share_open");
    // On mobile, prefer native share sheet if available
    const isMobile = /Mobi|Android|iPhone/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: SHARE_TITLE,
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
        trackEvent("share_success", { method: "native" });
        return;
      } catch (e) {
        // user cancelled or not allowed — fall through to modal
      }
    }
    setOpen(true);
  };

  const encodedUrl = encodeURIComponent(SHARE_URL);
  const encodedTitle = encodeURIComponent(SHARE_TITLE);
  const encodedText = encodeURIComponent(`${SHARE_TEXT}\n\n${SHARE_URL}`);

  const links = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedText}`,
      bg: "#25D366",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967c-.273-.099-.471-.148-.67.15c-.197.297-.767.966-.94 1.164c-.173.199-.347.223-.644.075c-.297-.15-1.255-.463-2.39-1.475c-.883-.788-1.48-1.761-1.653-2.059c-.173-.297-.018-.458.13-.606c.134-.133.298-.347.446-.52c.149-.174.198-.298.298-.497c.099-.198.05-.371-.025-.52c-.075-.149-.669-1.612-.916-2.207c-.242-.579-.487-.5-.669-.51a13 13 0 0 0-.57-.01c-.198 0-.52.074-.792.372c-.272.297-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074c.149.198 2.096 3.2 5.077 4.487c.709.306 1.262.489 1.694.625c.712.227 1.36.195 1.871.118c.571-.085 1.758-.719 2.006-1.413c.248-.694.248-1.289.173-1.413c-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214l-3.741.982l.998-3.648l-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884c2.64 0 5.122 1.03 6.988 2.898a9.83 9.83 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.82 11.82 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.9 11.9 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 0 0-3.48-8.413"/></svg>
      ),
    },
    {
      key: "email",
      label: "E-mail",
      href: `mailto:?subject=${encodedTitle}&body=${encodedText}`,
      bg: "#4A5D4E",
      icon: <Mail size={20} strokeWidth={1.8} />,
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      bg: "#1877F2",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978c.401 0 .955.042 1.468.103a8.7 8.7 0 0 1 1.141.195v3.325a8 8 0 0 0-.653-.036a26 26 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.69 1.69 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103l-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647"/></svg>
      ),
    },
    {
      key: "x",
      label: "X (Twitter)",
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      bg: "#0F1419",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26l8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      ),
    },
  ];

  return (
    <>
      <button
        data-testid="share-fab"
        onClick={handleNativeShare}
        aria-label="Deel deze woning"
        className="fixed bottom-20 right-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-ink text-white shadow-2xl transition-all hover:bg-brand-moss hover:scale-105 lg:bottom-8 lg:right-8"
      >
        <Share2 size={20} strokeWidth={1.8} />
      </button>

      {open && (
        <div
          data-testid="share-modal"
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
          onClick={() => setOpen(false)}
        >
          <div
            data-testid="share-modal-content"
            className="w-full max-w-md bg-brand-bg p-8 sm:m-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="overline">Delen</p>
                <h3 className="mt-3 font-serif text-3xl text-brand-ink">
                  Deel deze woning
                </h3>
              </div>
              <button
                data-testid="share-close"
                onClick={() => setOpen(false)}
                className="text-brand-muted hover:text-brand-ink"
              >
                <X size={22} />
              </button>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {links.map((l) => (
                <a
                  key={l.key}
                  data-testid={`share-${l.key}`}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => { trackEvent("share_success", { method: l.key }); setOpen(false); }}
                  className="group flex flex-col items-center gap-2 border border-brand-border p-4 transition-colors hover:border-brand-ink"
                >
                  <span
                    className="flex h-12 w-12 items-center justify-center rounded-full text-white transition-transform group-hover:scale-110"
                    style={{ background: l.bg }}
                  >
                    {l.icon}
                  </span>
                  <span className="text-xs font-medium text-brand-ink">
                    {l.label}
                  </span>
                </a>
              ))}
            </div>

            <button
              data-testid="share-copy"
              onClick={handleCopy}
              className="mt-6 flex w-full items-center justify-between border border-brand-border bg-brand-surface px-4 py-3 text-sm transition-colors hover:border-brand-moss"
            >
              <span className="truncate pr-3 text-brand-muted">{SHARE_URL}</span>
              <span className="inline-flex items-center gap-2 text-brand-moss">
                {copied ? (
                  <>
                    <Check size={16} /> Gekopieerd
                  </>
                ) : (
                  <>
                    <Copy size={16} /> Kopieer
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
